import styled from "styled-components";
import {
  Mail,
  Phone,
  MapPin,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
} from "lucide-react";

const FooterSection = styled.footer`
  background: #1a1a1a;
  color: #faf8f5;
  padding: 100px 24px 0;
`;

const Container = styled.div`
  max-width: 1300px;
  margin: 0 auto;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1.2fr;
  gap: 60px;
  margin-bottom: 80px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr 1fr;
    gap: 48px;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FooterColumn = styled.div`
  h3 {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 28px;
    color: #c9a86c;
    letter-spacing: 2px;
    text-transform: uppercase;
  }

  p {
    font-size: 14px;
    line-height: 1.9;
    color: #999;
    margin-bottom: 24px;
    font-weight: 300;
  }
`;

const Logo = styled.div`
  margin-bottom: 24px;

  img {
    height: 60px;
    width: auto;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 14px;
  color: #999;
  font-size: 14px;
  transition: all 0.3s;
  font-weight: 300;

  &:hover {
    color: #c9a86c;
  }

  svg {
    color: #c9a86c;
    min-width: 18px;
    width: 18px;
    height: 18px;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FooterLink = styled.a`
  color: #999;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 300;

  &:hover {
    color: #c9a86c;
    padding-left: 5px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const SocialIcon = styled.a`
  width: 44px;
  height: 44px;
  border: 1px solid #333;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: #c9a86c;
    border-color: #c9a86c;
    color: #1a1a1a;
    transform: translateY(-4px);
  }
`;

const Newsletter = styled.div`
  margin-top: 28px;
`;

const NewsletterForm = styled.form`
  display: flex;
  gap: 0;
  margin-top: 16px;
`;

const NewsletterInput = styled.input`
  flex: 1;
  padding: 14px 18px;
  border: 1px solid #333;
  border-right: none;
  background: #2a2a2a;
  color: #faf8f5;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #c9a86c;
  }

  &::placeholder {
    color: #666;
  }
`;

const SubscribeButton = styled.button`
  background: #c9a86c;
  color: #1a1a1a;
  border: 1px solid #c9a86c;
  padding: 14px 24px;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  letter-spacing: 1px;
  text-transform: uppercase;

  &:hover {
    background: transparent;
    color: #c9a86c;
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid #2a2a2a;
  padding: 36px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    text-align: center;
  }
`;

const Copyright = styled.div`
  font-size: 13px;
  color: #666;
  font-weight: 300;
`;

const FooterBottomLinks = styled.div`
  display: flex;
  gap: 32px;

  a {
    font-size: 13px;
    color: #666;
    transition: color 0.3s;
    font-weight: 300;

    &:hover {
      color: #c9a86c;
    }
  }
`;

const Footer = () => {
  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    console.log("[v0] Newsletter subscription:", email);
    alert(`Thank you for subscribing with ${email}!`);
    e.target.reset();
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <FooterSection id="contact">
      <Container>
        <FooterGrid>
          <FooterColumn>
            <Logo>
              <img src="/images/logo-light.png" alt="Hebe Hotel" />
            </Logo>
            <p>
              A modern eco hostel in the heart of the city where nature meets
              technology. Experience sustainable urban travel.
            </p>
            <SocialLinks>
              <SocialIcon href="#" aria-label="Facebook">
                <Facebook size={20} />
              </SocialIcon>
              <SocialIcon href="#" aria-label="Instagram">
                <Instagram size={20} />
              </SocialIcon>
              <SocialIcon href="#" aria-label="Twitter">
                <Twitter size={20} />
              </SocialIcon>
              <SocialIcon href="#" aria-label="LinkedIn">
                <Linkedin size={20} />
              </SocialIcon>
            </SocialLinks>
          </FooterColumn>

          <FooterColumn>
            <h3>Quick Links</h3>
            <FooterLinks>
              <FooterLink onClick={() => scrollToSection("about")}>
                About Us
              </FooterLink>
              <FooterLink onClick={() => scrollToSection("rooms")}>
                Rooms
              </FooterLink>
              <FooterLink onClick={() => scrollToSection("facilities")}>
                Facilities
              </FooterLink>
              <FooterLink onClick={() => scrollToSection("gallery")}>
                Gallery
              </FooterLink>
              <FooterLink onClick={() => scrollToSection("booking")}>
                Book Now
              </FooterLink>
            </FooterLinks>
          </FooterColumn>

          <FooterColumn>
            <h3>Services</h3>
            <FooterLinks>
              <FooterLink href="#">Restaurant</FooterLink>
              <FooterLink href="#">Spa & Wellness</FooterLink>
              <FooterLink href="#">Events</FooterLink>
              <FooterLink href="#">Coworking</FooterLink>
              <FooterLink href="#">Airport Transfer</FooterLink>
            </FooterLinks>
          </FooterColumn>

          <FooterColumn>
            <h3>Contact Us</h3>
            <ContactInfo>
              <ContactItem href="mailto:admin@hostelnmc.com">
                <Mail size={20} />
                <span>admin@hostelnmc.com</span>
              </ContactItem>
              <ContactItem href="tel:+62xxx">
                <Phone size={20} />
                <span>+62 XXX XXXX XXXX</span>
              </ContactItem>
              <ContactItem href="#">
                <MapPin size={20} />
                <span>Bogor, West Java, Indonesia</span>
              </ContactItem>
            </ContactInfo>
            <Newsletter>
              <p style={{ fontSize: "14px", marginBottom: "12px" }}>
                Subscribe to our newsletter
              </p>
              <NewsletterForm onSubmit={handleNewsletterSubmit}>
                <NewsletterInput
                  type="email"
                  name="email"
                  placeholder="Your email"
                  required
                />
                <SubscribeButton type="submit">Subscribe</SubscribeButton>
              </NewsletterForm>
            </Newsletter>
          </FooterColumn>
        </FooterGrid>

        <FooterBottom>
          <Copyright>
            © 2026 Hebe Hotel, Hostel & Hospitality Network. All rights
            reserved.
          </Copyright>
          <FooterBottomLinks>
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a>
            <a href="#">Cookie Policy</a>
          </FooterBottomLinks>
        </FooterBottom>
      </Container>
    </FooterSection>
  );
};

export default Footer;
